import type { ClientFlowAction } from '~/graphql/types.client';

import {
  DirectoryMap,
  generateDirectoryMap,
} from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getDirectory';
import {
  generateInstanceMap,
  InstanceMap,
} from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getInstance';
import getParentInstances from '../../getParentInstances';
import { isNil } from 'ramda';
import convertTemplateStringToLabel from '../convertTemplateStringToLabel';
import type { RelativeMaps } from '~/components/page/Automation/v2/types';

type GetRelativeMapsArgs = {
  /** The action until which the instances need to be collected */
  action?: ClientFlowAction;

  actions: Array<ClientFlowAction>;

  maps: RelativeMaps;
};

/**
 * Gets current maps and updates some of the fields
 */
const getUpToDateRelativeMaps = ({
  action,
  actions,
  maps,
}: GetRelativeMapsArgs): RelativeMaps => {
  const {
    subjectMap,
    instanceMap,
    primitiveInputMap,
    conditionMap,
    subjectToConditionMap,
    subjects,
    instances,
  } = maps;

  // instances with optional extraDescription field
  const actionInstances = getParentInstances({
    action,
    allActions: actions,
    subjectMap,
  });

  const { updatedCompleteInstanceMap, updatedRelativeDirectoryMap } = (() => {
    const onlyInstances = actionInstances.map(i => i.instance);

    // first get the initial relative maps. Once we have access to relative maps we can determine the path for pointers
    const relativeInstanceMap = generateInstanceMap(onlyInstances);
    const completeInstanceMap: InstanceMap = {
      ...instanceMap,
      ...relativeInstanceMap,
    };
    const relativeDirectoryMap: DirectoryMap = generateDirectoryMap(
      subjects,
      [...onlyInstances, ...instances],
      subjectMap,
    );

    // use extraDescription field to create labels for pointers and then throw extraDescription
    // as it is not needed anymore
    const updatedInstancesWithPointerLabels = actionInstances.map(a => ({
      ...a.instance,
      dir: a.instance.dir.map((d, index) => {
        // inserted to the first directory item
        if (
          index === 0 &&
          !isNil(a.extraDescription) &&
          a.extraDescription.template
        ) {
          const addition = convertTemplateStringToLabel({
            templateString: a.extraDescription.template,
            mappings: a.extraDescription.mappings,
            directoryMap: relativeDirectoryMap,
            instanceMap: completeInstanceMap,
            subjectMap,
          });

          return { ...d, label: d.label + ' ' + addition };
        }

        return d;
      }),
    }));

    return {
      updatedCompleteInstanceMap: {
        ...instanceMap,
        ...generateInstanceMap(updatedInstancesWithPointerLabels),
      },
      updatedRelativeDirectoryMap: generateDirectoryMap(
        subjects,
        [...updatedInstancesWithPointerLabels, ...instances],
        subjectMap,
      ),
    };
  })();

  return {
    instanceMap: updatedCompleteInstanceMap,
    directoryMap: updatedRelativeDirectoryMap,
    subjectMap,
    primitiveInputMap,
    conditionMap,
    subjectToConditionMap,
    instances,
    subjects,
  };
};

export default getUpToDateRelativeMaps;
